/**
 * TeamInviteAcceptPage
 * Public page for accepting team invitations via magic link token.
 *
 * URL: /accept-invitation?token=<jwt>&role=<role>
 *
 * Flow:
 * 1. Reads ?token= from URL params
 * 2. Calls GET /api/settings/team/accept?token=<token>
 * 3. Shows welcome message with role
 * 4. Provides "Create Account" / "Sign In" button to existing login flow
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Check,
  Loader2,
  AlertCircle,
  LogIn,
  Shield,
  UserPlus,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Role display names
const ROLE_LABELS = {
  AD: 'Athletic Director',
  headCoach: 'Head Coach',
  assistantCoach: 'Assistant Coach',
  analyst: 'Analyst',
  recruiter: 'Recruiter',
  player: 'Player',
  admin: 'Administrator',
  superadmin: 'Super Administrator',
};

export function TeamInviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const roleHint = searchParams.get('role');

  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please check your invitation link.');
      setIsLoading(false);
      return;
    }

    const acceptInvite = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/settings/team/accept?token=${encodeURIComponent(token)}`);
        const json = await resp.json();

        if (!resp.ok || !json.success) {
          setError(json.error || 'Failed to verify invitation.');
          return;
        }

        setResult(json.data);
      } catch (err) {
        console.error('[TeamInviteAccept] Error:', err);
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    acceptInvite();
  }, [token]);

  const handleSignIn = () => {
    // Store sport from invitation so post-login redirects to the right dashboard
    if (result?.sport) {
      localStorage.setItem('slam_sport_selection', result.sport);
      localStorage.setItem('slam_sport_selected', 'true');
    }
    sessionStorage.setItem('redirectAfterLogin', result?.sport ? `/${result.sport}/canvas` : '/draft-day');
    navigate('/login');
  };

  const roleLabel = result?.role
    ? ROLE_LABELS[result.role] || result.role
    : roleHint
      ? ROLE_LABELS[roleHint] || roleHint
      : 'Team Member';

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-amber-400" />
          <p className="text-zinc-400 text-sm">
            Verifying your invitation...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-black">
        <div className="max-w-md w-full p-8 rounded-2xl border bg-zinc-950 border-zinc-800">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-500/20">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold mb-2 text-white text-center">
            Invitation Error
          </h1>
          <p className="mb-6 text-zinc-400 text-center text-sm">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-medium transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-black">
      <div className="max-w-md w-full p-8 rounded-2xl border bg-zinc-950 border-zinc-800">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-green-500/20">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-white">
            Welcome to SLAM!
          </h1>
          <p className="text-zinc-400">
            Your invitation has been accepted.
          </p>
        </div>

        {/* Invitation details */}
        <div className="p-4 rounded-xl mb-6 bg-zinc-900 border border-zinc-800">
          {/* Email */}
          {result?.email && (
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-400/10">
                <UserPlus className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Invited Email</p>
                <p className="font-medium text-white">{result.email}</p>
              </div>
            </div>
          )}

          {/* Role */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-400/10">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Your Role</p>
              <p className="font-medium text-white">{roleLabel}</p>
            </div>
          </div>
        </div>

        {/* Call to action */}
        <div className="space-y-3">
          <button
            onClick={handleSignIn}
            className="w-full py-3 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Create Account or Sign In
          </button>
          <p className="text-center text-xs text-zinc-500">
            Sign in to access the SLAM Sports AI platform with your new role.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TeamInviteAcceptPage;
